// -----------------------------------------------------------------------------
// CLASH REPORT IMPORT (Part 4) — reads the office's Excel clash tracker (e.g. "Clash Report No 1.xlsx")
// in the browser, with no extra library: an .xlsx file is a zip of XML files.
//   1. unzip()            — only the few XML files we need (the pictures, megabytes, are never unpacked)
//   2. readWorkbook()     — sheet names → cells (text, numbers; cached results of formulas)
//   3. parseClashReport() — finds "tracker" sheets (header row "Issues No. | Discipline | Gridline | …") and
//                           turns each issue block into one ImportIssue for issues_import (0010).
// Tracker layout (one block per issue, block starts at the code in column A):
//   row r     : code · discipline · gridline · target date · RFI ref · … · status
//   below     : description (Discipline column, every line down to "Snapshots"), resolution log (Resolutions column:
//               a date in F, then its lines from F–I, down to "Resolution Snapshot" — v0.8.1 resolutionLog),
//               drawing references (under "Drawings Reference"). v0.9.2: Notes under Status are not imported.
// Pictures (v0.7.0, Kyle 18/Oct/26: split by section): readPictures() finds every picture on a sheet and the cell it
// sits in; attachPictures() gives each issue the pictures inside its block — above "Resolution Snapshot" = the
// issue's snapshots, below it = the resolution picture (goes to the newest log entry). The bytes are unpacked only
// when importing (readMedia), then shrunk and uploaded like any snapshot (components/ImportReport.tsx).
// Unit-tested in tests/unit/clashReport.test.ts.
// -----------------------------------------------------------------------------

export interface ImportLog { date: string | null; note: string; action_by: string }
export interface ImportIssue {
  sheet: string; prefix: string; seq: number; code: string; discipline: string;
  status: "Open" | "Critical" | "Closed"; raw_status: string; description: string; gridline: string;
  rfi_ref: string; drawing_ref: string; target_date: string | null; created_on: string | null; log: ImportLog[];
  /** rows of the issue's block (for pictures): code row, last row, "Resolution Snapshot" row */
  rows?: { start: number; end: number; res: number };
  /** v0.7.0: zip paths of the pictures in the block (issue snapshots / resolution) */
  pictures?: { issue: string[]; resolution: string[] };
}
export interface TrackerSheet { name: string; title: string; report_date: string | null; issues: ImportIssue[] }
export interface ClashReport { sheets: TrackerSheet[]; other: string[] }

export type Cell = string | number | boolean | null;
export type Grid = Map<string, Cell>;                 // "A6" → value
export interface Workbook { names: string[]; sheets: Map<string, Grid> }

// ---------------------------------------------------------------- unzip
const u16 = (b: Uint8Array, o: number) => b[o] | (b[o + 1] << 8);
const u32 = (b: Uint8Array, o: number) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0;

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** The zip entries whose names pass `want`, unpacked. */
export async function unzip(buf: ArrayBuffer | Uint8Array, want: (name: string) => boolean): Promise<Map<string, Uint8Array>> {
  const b = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let eocd = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65557); i--) if (u32(b, i) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error("This is not an Excel (.xlsx) file");
  const total = u16(b, eocd + 10);
  let p = u32(b, eocd + 16);
  const out = new Map<string, Uint8Array>();
  const dec = new TextDecoder();
  for (let k = 0; k < total; k++) {
    if (u32(b, p) !== 0x02014b50) throw new Error("The .xlsx file looks damaged");
    const method = u16(b, p + 10), size = u32(b, p + 20), nameLen = u16(b, p + 28), extra = u16(b, p + 30), comment = u16(b, p + 32);
    const local = u32(b, p + 42);
    const name = dec.decode(b.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extra + comment;
    if (!want(name)) continue;
    const start = local + 30 + u16(b, local + 26) + u16(b, local + 28);
    const raw = b.subarray(start, start + size);
    if (method === 0) out.set(name, raw.slice());
    else if (method === 8) out.set(name, await inflateRaw(raw));
    else throw new Error(`Unsupported compression in ${name}`);
  }
  return out;
}

// ---------------------------------------------------------------- xml → cells
const ENT: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" };
export const unxml = (s: string) => s.replace(/&(#x[0-9a-f]+|#\d+|\w+);/gi, (m, e: string) =>
  e[0] === "#" ? String.fromCodePoint(e[1].toLowerCase() === "x" ? parseInt(e.slice(2), 16) : Number(e.slice(1))) : ENT[e] ?? m);
const attrs = (s: string): Record<string, string> => {
  const o: Record<string, string> = {};
  for (const m of s.matchAll(/([\w:]+)="([^"]*)"/g)) o[m[1]] = unxml(m[2]);
  return o;
};
/** Text of every <t> inside a fragment (rich text runs joined). */
const texts = (s: string) => [...s.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map((m) => unxml(m[1])).join("");

/** v0.9.2 (round 33 #4): cells whose formula uses TODAY() / NOW() — ignored only where a REPORT DATE is read
 *  (the sheet's DATE row, the cover's Date); everywhere else the calculated value is used as in the file. */
export const todayCells = new WeakMap<Grid, Set<string>>();
const isTodayCell = (g: Grid | undefined, col: number, row: number) => !!g && !!todayCells.get(g)?.has(`${colName(col)}${row}`);

export function parseSheetXml(xml: string, shared: string[]): Grid {
  const g: Grid = new Map();
  const today = new Set<string>();
  todayCells.set(g, today);
  for (const m of xml.matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
    const a = attrs(m[1]); const body = m[2] ?? "";
    if (!a.r) continue;
    const v = /<v>([\s\S]*?)<\/v>/.exec(body)?.[1];
    // =TODAY() / =NOW() hold the day the file was last opened: remembered, so a report date read from here is ignored
    if (/<f\b[^>]*>[^<]*\b(TODAY|NOW)\s*\(/i.test(body)) today.add(a.r);
    let val: Cell = null;
    if (a.t === "s") val = v === undefined ? null : shared[Number(v)] ?? null;
    else if (a.t === "inlineStr") val = texts(body);
    else if (a.t === "str" || a.t === "e") val = v === undefined ? null : unxml(v);
    else if (a.t === "b") val = v === "1";
    else if (v !== undefined && v !== "") val = Number(v);
    if (val !== null && val !== "") g.set(a.r, val);
  }
  return g;
}

/** Read sheet names and cells from .xlsx bytes. */
export async function readWorkbook(buf: ArrayBuffer | Uint8Array): Promise<Workbook> {
  const want = (n: string) => n === "xl/workbook.xml" || n === "xl/_rels/workbook.xml.rels" || n === "xl/sharedStrings.xml" || /^xl\/worksheets\/[^/]+\.xml$/.test(n);
  const files = await unzip(buf, want);
  const dec = new TextDecoder();
  const txt = (n: string) => (files.has(n) ? dec.decode(files.get(n)) : "");
  const wb = txt("xl/workbook.xml");
  if (!wb) throw new Error("This is not an Excel (.xlsx) file");
  const rels = new Map([...txt("xl/_rels/workbook.xml.rels").matchAll(/<Relationship\b([^>]*)\/?>/g)].map((m) => { const a = attrs(m[1]); return [a.Id, a.Target] as const; }));
  const shared = [...txt("xl/sharedStrings.xml").matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => texts(m[1]));
  const names: string[] = []; const sheets = new Map<string, Grid>();
  for (const m of wb.matchAll(/<sheet\b([^>]*)\/?>/g)) {
    const a = attrs(m[1]);
    const target = rels.get(a["r:id"]) ?? "";
    const path = target.startsWith("/") ? target.slice(1) : `xl/${target}`;
    names.push(a.name);
    sheets.set(a.name, files.has(path) ? parseSheetXml(dec.decode(files.get(path)), shared) : new Map());
  }
  return { names, sheets };
}

// ---------------------------------------------------------------- helpers
const COLS = (ref: string) => /^([A-Z]+)(\d+)$/.exec(ref);
export const colNum = (c: string) => [...c].reduce((n, ch) => n * 26 + ch.charCodeAt(0) - 64, 0);
export const colName = (n: number): string => (n <= 0 ? "" : colName(Math.floor((n - 1) / 26)) + String.fromCharCode(65 + ((n - 1) % 26)));
const str = (v: Cell) => (v === null || v === undefined ? "" : String(v).replace(/\s+/g, " ").trim());
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const iso = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** Excel day number (1900 system) → ISO date. */
export function excelDate(n: number): string {
  const ms = Math.round((n - 25569) * 86400000);
  return new Date(ms).toISOString().slice(0, 10);
}
/** A cell that holds a date: an Excel day number, or text like "7th January 2026", "21 Jan 2026", "07/01/2026". */
export function cellDate(v: Cell): string | null {
  if (typeof v === "number") return v > 20000 && v < 80000 ? excelDate(v) : null;
  const s = str(v);
  let m = /(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,})\.?,?\s+(\d{4})/.exec(s);
  if (m) { const mo = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase()) + 1; if (mo) return iso(Number(m[3]), mo, Number(m[1])); }
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
  if (m) return iso(Number(m[3]), Number(m[2]), Number(m[1]));
  m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return iso(Number(m[1]), Number(m[2]), Number(m[3]));
  return null;
}
export function normaliseStatus(s: string): "Open" | "Critical" | "Closed" {
  const t = s.trim().toLowerCase();
  return t.startsWith("clos") ? "Closed" : t.startsWith("crit") ? "Critical" : "Open";
}
const CODE_RE = /^([A-Z][A-Z0-9]{1,3})-(\d{1,3})$/;

/** Rows of a grid: row number → (column number → value). */
function rowsOf(g: Grid): Map<number, Map<number, Cell>> {
  const rows = new Map<number, Map<number, Cell>>();
  for (const [ref, v] of g) {
    const m = COLS(ref); if (!m) continue;
    const r = Number(m[2]);
    if (!rows.has(r)) rows.set(r, new Map());
    rows.get(r)!.set(colNum(m[1]), v);
  }
  return rows;
}

// ---------------------------------------------------------------- the clash tracker
const LABELS = /^(issues|notes|drawings reference|snapshots|resolution snapshot|resolutions)$/i;

/** One tracker sheet → issues. Returns null when the sheet is not a tracker (no "Issues No." header). */
export function parseTracker(name: string, g: Grid, fallbackDate: string | null): TrackerSheet | null {
  const rows = rowsOf(g);
  const nums = [...rows.keys()].sort((a, b) => a - b);
  const header = nums.find((r) => /^issues? no/i.test(str(rows.get(r)!.get(1) ?? null)));
  if (header === undefined) return null;
  const col: Record<string, number> = {};
  for (const [c, v] of rows.get(header)!) {
    const t = str(v).toLowerCase();
    if (t.startsWith("discipline") && !col.disc) col.disc = c;
    else if (t.startsWith("gridline")) col.grid = c;
    else if (t.startsWith("target")) col.target = c;
    else if (t.startsWith("rfi")) col.rfi = c;
    else if (t.startsWith("resolution")) col.res = c;
    else if (t.startsWith("action")) col.action = c;
    else if (t === "status" && !col.status) col.status = c;
  }
  if (!col.disc || !col.status || !col.res) return null;   // summary sheets (no Resolutions column) are not trackers
  const at = (r: number, c: number | undefined): Cell => (c ? rows.get(r)?.get(c) ?? null : null);
  // title and date (rows above the header): "TITLE : …", "DATE: …"
  let title = "", date: string | null = null;
  for (const r of nums.filter((r) => r < header)) {
    const k = str(at(r, 1)).toLowerCase();
    if (k.startsWith("title")) title = str(at(r, 2));
    if (k.startsWith("date")) date = isTodayCell(g, 2, r) ? null : cellDate(at(r, 2));
  }
  date = date ?? fallbackDate;
  const starts = nums.filter((r) => r > header && CODE_RE.test(str(at(r, 1))));
  const issues: ImportIssue[] = starts.map((r, i) => {
    const end = (starts[i + 1] ?? Number.MAX_SAFE_INTEGER) - 1;
    const inBlock = nums.filter((x) => x > r && x <= end);
    const [, prefix, seq] = CODE_RE.exec(str(at(r, 1)))!;
    const hasLabel = (x: number, re: RegExp) => [...(rows.get(x) ?? new Map()).values()].some((v) => re.test(str(v)));
    // the picture area starts at the first "Snapshots" label (about 14 rows down); the resolution log runs on
    // down to its own "Resolution Snapshot" label (about 31 rows down) — round 18 #5
    const snapStop = inBlock.find((x) => hasLabel(x, /^snapshots?$/i)) ?? end + 1;
    const resStop = inBlock.find((x) => /^(resolution )?snapshots?$/i.test(str(at(x, col.res)))) ?? end + 1;
    // description: every line in the Discipline column under the code row, down to "Snapshots", one line each (round 18 #4)
    const description = inBlock.filter((x) => x < snapStop).map((x) => str(at(x, col.disc))).filter((t) => t && !LABELS.test(t)).join("\n");
    // drawing references: under the "Drawings Reference" label, same column, until "Snapshots"
    const drawings: string[] = [];
    const labelRow = inBlock.find((x) => hasLabel(x, /^drawings? reference/i));
    if (labelRow !== undefined) {
      const lc = [...rows.get(labelRow)!].find(([, v]) => /^drawings? reference/i.test(str(v)))![0];
      for (const x of inBlock.filter((y) => y > labelRow)) {
        const t = str(at(x, lc));
        if (/^snapshots?$/i.test(str(at(x, lc - 1))) || /^snapshots?$/i.test(t)) break;
        if (t && !LABELS.test(t)) drawings.push(t);
        if (x > labelRow + 8) break;
      }
    }
    const log = resolutionLog(inBlock.filter((y) => y < resStop), at, col.res, col.action);
    // v0.9.2 (round 33 #5, Kyle 03/Nov/26): the resolution log takes only the dated entries under the Resolutions header —
    // no "Notes" (under Status) and no "Status in the report" lines
    const raw = str(at(r, col.status));
    return {
      sheet: name, prefix, seq: Number(seq), code: `${prefix}-${seq.padStart(2, "0")}`, discipline: str(at(r, col.disc)).toUpperCase(),
      status: normaliseStatus(raw), raw_status: raw, description, gridline: str(at(r, col.grid)), rfi_ref: str(at(r, col.rfi)),
      drawing_ref: drawings.join("; "), target_date: cellDate(at(r, col.target)), created_on: date,
      log: log.filter((l) => l.note.trim() !== ""),
      rows: { start: r, end: starts[i + 1] ? end : Number.MAX_SAFE_INTEGER, res: inBlock.find((x) => hasLabel(x, /^resolution snapshots?$/i)) ?? end + 1 },
    };
  });
  return { name, title, report_date: date, issues };
}

/** Columns that hold resolution text: Resolutions (F) and the next three (G, H, I), stopping before Action by. */
export function resolutionCols(res: number, action?: number): number[] {
  const last = Math.min(res + 3, action && action > res ? action - 1 : res + 3);
  return Array.from({ length: last - res + 1 }, (_, k) => res + k);
}

/** v0.8.1 (round 29 #2, Kyle 30/Oct/26): the resolution log of one issue block.
 *  - a date in the Resolutions column (F) starts an entry (its Action by from the same row);
 *  - every following row adds its text — from F, G, H or I, whichever cells have text — as a new line of that entry,
 *    until the next date; empty cells are ignored, so blank rows add no empty lines;
 *  - rows above the first date are skipped; label cells ("Snapshots" …) are never text. */
export function resolutionLog(blockRows: number[], at: (r: number, c: number | undefined) => Cell, res: number, action?: number): ImportLog[] {
  const cols = resolutionCols(res, action);
  const log: ImportLog[] = [];
  for (const x of blockRows) {
    const v = at(x, res); const t = str(v);
    const d = t && !LABELS.test(t) ? cellDate(v) : null;
    const isDate = !!d && (typeof v === "number" || t.length <= 30);
    if (isDate) log.push({ date: d, note: "", action_by: str(at(x, action)) });
    const last = log[log.length - 1];
    if (!last) continue;                                          // above the first date: skipped
    const texts = cols.slice(isDate ? 1 : 0).map((c) => str(at(x, c))).filter((s) => s && !LABELS.test(s));
    for (const line of texts) last.note = last.note ? `${last.note}\n${line}` : line;
    if (!last.action_by) last.action_by = str(at(x, action));
  }
  return log;
}

/** Date on the cover sheet that goes with a tracker sheet: "Cover" for "X", "Cover (2)" for "X (2)". */
function coverDate(wb: Workbook, sheet: string): string | null {
  const suffix = / \(\d+\)$/.exec(sheet)?.[0] ?? "";
  const cover = wb.names.find((n) => n.toLowerCase() === `cover${suffix}`);
  if (!cover) return null;
  const grid = wb.sheets.get(cover)!;
  const rows = rowsOf(grid);
  for (const r of [...rows.keys()].sort((a, b) => a - b)) {
    const row = rows.get(r)!;
    for (const [c, v] of row) if (/^date\s*:?$/i.test(str(v)) && !isTodayCell(grid, c + 1, r)) { const d = cellDate(row.get(c + 1) ?? null); if (d) return d; }
  }
  return null;
}

export function parseClashReport(wb: Workbook): ClashReport {
  const sheets: TrackerSheet[] = []; const other: string[] = [];
  for (const n of wb.names) {
    const t = parseTracker(n, wb.sheets.get(n)!, coverDate(wb, n));
    if (t && t.issues.length) {
      if (!t.report_date) t.report_date = coverDate(wb, n);
      for (const i of t.issues) { i.created_on = i.created_on ?? t.report_date; for (const l of i.log) l.date = l.date ?? t.report_date; }
      sheets.push(t);
    } else other.push(n);
  }
  return { sheets, other };
}

/** v0.9.1 (round 32 #1–#3): later copies of a code already on the same sheet (a typo in the report). Only the first is
 *  imported; the import pop-up shows the others as Error rows. A code on two different sheets is fine (a later report updates). */
export function duplicateRows(sheets: TrackerSheet[]): Set<ImportIssue> {
  const d = new Set<ImportIssue>();
  for (const s of sheets) { const seen = new Set<string>(); for (const r of s.issues) { if (seen.has(r.code)) d.add(r); seen.add(r.code); } }
  return d;
}

/** What issues_import receives for the ticked sheets, in workbook order (a later report updates an earlier one). */
export const importPayload = (sheets: TrackerSheet[]) => sheets.flatMap((s) => s.issues.map(({ sheet: _s, code: _c, raw_status: _r, rows: _w, pictures: _p, ...rest }) => rest));

// ---------------------------------------------------------------- pictures (v0.7.0)
/** A picture on a sheet: the cell its top-left corner sits in (1-based) and its file inside the .xlsx. */
export interface SheetPicture { row: number; col: number; media: string }
const resolve = (from: string, target: string) => {
  if (target.startsWith("/")) return target.slice(1);
  const parts = from.split("/").slice(0, -1);
  for (const p of target.split("/")) { if (p === "..") parts.pop(); else if (p !== ".") parts.push(p); }
  return parts.join("/");
};
const relsOf = (xml: string) => new Map([...xml.matchAll(/<Relationship\b([^>]*)\/?>/g)].map((m) => { const a = attrs(m[1]); return [a.Id, a.Target] as const; }));

/** Sheet name → its pictures, top to bottom. Only small XML files are unpacked here. */
export async function readPictures(buf: ArrayBuffer | Uint8Array): Promise<Map<string, SheetPicture[]>> {
  const files = await unzip(buf, (n) => n === "xl/workbook.xml" || n === "xl/_rels/workbook.xml.rels" || /^xl\/(worksheets|drawings)\/_rels\/[^/]+\.rels$/.test(n) || /^xl\/drawings\/[^/]+\.xml$/.test(n));
  const dec = new TextDecoder();
  const txt = (n: string) => (files.has(n) ? dec.decode(files.get(n)) : "");
  const wbRels = relsOf(txt("xl/_rels/workbook.xml.rels"));
  const out = new Map<string, SheetPicture[]>();
  for (const m of txt("xl/workbook.xml").matchAll(/<sheet\b([^>]*)\/?>/g)) {
    const a = attrs(m[1]);
    const sheet = resolve("xl/workbook.xml", wbRels.get(a["r:id"]) ?? "");
    const sheetRels = relsOf(txt(sheet.replace(/([^/]+)$/, "_rels/$1.rels")));
    const pics: SheetPicture[] = [];
    for (const target of sheetRels.values()) {
      const drawing = resolve(sheet, target);
      if (!/drawings\/drawing\d*\.xml$/.test(drawing)) continue;
      const dRels = relsOf(txt(drawing.replace(/([^/]+)$/, "_rels/$1.rels")));
      for (const an of txt(drawing).matchAll(/<xdr:(?:twoCellAnchor|oneCellAnchor)\b[\s\S]*?<\/xdr:(?:twoCellAnchor|oneCellAnchor)>/g)) {
        const from = /<xdr:from>[\s\S]*?<xdr:col>(\d+)<\/xdr:col>[\s\S]*?<xdr:row>(\d+)<\/xdr:row>/.exec(an[0]);
        const embed = /<a:blip\b[^>]*r:embed="([^"]+)"/.exec(an[0]);
        if (!from || !embed || !dRels.get(embed[1])) continue;
        pics.push({ col: Number(from[1]) + 1, row: Number(from[2]) + 1, media: resolve(drawing, dRels.get(embed[1])!) });
      }
    }
    out.set(a.name, pics.sort((p, q) => p.row - q.row || p.col - q.col));
  }
  return out;
}

/** Give every issue the pictures inside its block: above "Resolution Snapshot" → issue snapshots; below → resolution. */
export function attachPictures(report: ClashReport, pics: Map<string, SheetPicture[]>): ClashReport {
  for (const s of report.sheets) {
    const list = pics.get(s.name) ?? [];
    for (const i of s.issues) {
      if (!i.rows) continue;
      const mine = list.filter((p) => p.row >= i.rows!.start && p.row <= i.rows!.end);
      i.pictures = { issue: mine.filter((p) => p.row < i.rows!.res).map((p) => p.media), resolution: mine.filter((p) => p.row >= i.rows!.res).map((p) => p.media) };
    }
  }
  return report;
}

/** Pictures to bring in for the ticked sheets: per code, from the LAST ticked report that has pictures for it. */
export function picturesToImport(sheets: TrackerSheet[]): Map<string, { issue: string[]; resolution: string[] }> {
  const out = new Map<string, { issue: string[]; resolution: string[] }>();
  for (const s of sheets) for (const i of s.issues) if (i.pictures && (i.pictures.issue.length || i.pictures.resolution.length)) out.set(i.code, i.pictures);
  return out;
}

/** Unpack just these pictures (zip path → bytes). */
export const readMedia = (buf: ArrayBuffer | Uint8Array, paths: string[]) => unzip(buf, (n) => paths.includes(n));
export const mediaType = (path: string) => (/\.png$/i.test(path) ? "image/png" : /\.(jpe?g)$/i.test(path) ? "image/jpeg" : /\.webp$/i.test(path) ? "image/webp" : /\.gif$/i.test(path) ? "image/gif" : /\.bmp$/i.test(path) ? "image/bmp" : "");
